import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { getProjectQuestionsService, addNewProjectService, updateProjectService, addProjectAnswersService } from '../../utils/projectService'
import { addProject, answerQuestion } from '../../reducers/projectReducer'
import LoadingContainer from '../Shared/LoadingContainer'
import QuestionIcons from './QuestionIcons'

export default function ProjectCreation() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getToken, userId } = useAuth();

  const [projectTitle, setProjectTitle] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [projectDueDate, setProjectDueDate] = useState('')
  const [currentAnswer, setCurrentAnswer] = useState('')
  const [projectLoading, setProjectLoading] = useState(false)
  const { currentProject } = useSelector((state) => state.projectList)
  const { step, projectQuestions, userAnswers } = currentProject || {}

  const handleAddProject = async () => {
    if (projectTitle) {
      setProjectLoading(true);

      try {
        const response = await getProjectQuestionsService(getToken, { 
          details: { 
            projectTitle, 
            projectDescription, 
            projectDueDate 
          } 
        });

        const projectId = `project_${Math.random().toString(36).substring(2, 15)}`;

        const newProject = await addNewProjectService(getToken, {
          userId,
          projectId,
          title: projectTitle,
          description: projectDescription,
          dueDate: projectDueDate,
          step: 'gathering_info',
          projectQuestions: response.project_questions,
        });

        if (!newProject.status === 200) {
          throw new Error('Failed to add new project');
        }

        dispatch(addProject(newProject.data))
        setProjectLoading(false);
      } catch (error) {
        setProjectLoading(false);
        console.error('Error getting AI response:', error);
      }
    }
  }

  const handleAnswerQuestion = async () => {
    if (!currentAnswer.trim()) {
      alert('Answer cannot be empty.');
      return;
    }

    const currentQuestion = projectQuestions[userAnswers.length];

    const answerWithQuestionId = {
      questionId: currentQuestion.id,
      answer: currentAnswer.trim(),
    };

    dispatch(answerQuestion(answerWithQuestionId));
    setCurrentAnswer('');

    if (userAnswers.length + 1 === projectQuestions.length) {
      try {
        await updateProjectService(getToken, currentProject?.id, {
          ...currentProject,
          step: 'creating_tasks'
        });  
  
        await addProjectAnswersService(getToken, currentProject?.id, {
          userAnswers: [...userAnswers, answerWithQuestionId],
        });
        navigate(`/project/${currentProject?.id}`);
      } catch (error) {
        console.error('Error submitting project answers:', error);
      }
    }
  };
      
  const handleAddQuestions = () => {
    if (!currentProject.projectQuestions) {
      alert('Will need to fix this at some point');
    }
  }

  if (step === '') {
    return (
      <>
          {projectLoading ? (
            <LoadingContainer />
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-6">Initiate a New Project</h1>
              
              <div className="space-y-2 pb-4">
                <p className="text-gray-600">Pray, bestow a suitable title upon your project <span className='text-sm'>(required, naturally).</span></p>
                <input
                  placeholder={`Your title should start with a verb ie "Create a website", "Write a book", "Build a house"`}
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  data-amp-track-section="Project Creation"
                  data-amp-track-action="Project_Title_Change"
                  data-amp-track-description="Project Title Change"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
              />
              </div>
              <div className="space-y-2 pb-2">
                <p className="text-gray-600">Project Description <span className='text-sm'>(optional)</span></p>
                <textarea
                  placeholder="Summarize your project in one or two sentences. Brevity is the soul of wit."
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  data-amp-track-section="Project Creation"
                  data-amp-track-action="Project_Description_Change"
                  data-amp-track-description="Project Description Change"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="space-y-2 pb-4">
                <p className="text-gray-600">When shall the due date be? <span className='text-sm'>(optional)</span></p>
                <input
                  type="date"
                  value={projectDueDate}
                  onChange={(e) => setProjectDueDate(e.target.value)}
                  data-amp-track-section="Project Creation"
                  data-amp-track-action="Project_Due_Date_Change"
                  data-amp-track-description="Project Due Date Change"
                  className="w-30 p-2 border border-gray-300 rounded-md text-sm"
                /> 
              </div>
              <button onClick={handleAddProject} 
                data-amp-track-section="Project Creation"
                data-amp-track-action="Proceed_Click"
                data-amp-track-description="Proceed to Project Creation"
                className="ml-0 mt-4 w-full text-lg font-bold p-5 bg-slate-800 hover:bg-slate-700 hover:text-white text-white border rounded-md">
                Proceed
              </button> 
            </>
          )}
      </>
    )
  }
  
  if (step === 'gathering_info') {
    return (
      <>
        <h2 className="text-xl font-bold pb-0 mb-0">{currentProject.title}</h2>
        {currentProject.description && <p className="text-sm font-normal p-0 m-0">{currentProject.description}</p>}

        <h3 className="text-xl font-bold pt-8">Let us refine the details of your project</h3>
    
        <div className="space-y-2">
          {!projectQuestions ? (
            <div>
              Oops, something went wrong. 
              <button onClick={() => handleAddQuestions()}>Try again</button>
            </div>
          ) : (
            <>
              <h3 className="text-md font-normal mb-10">Indulge me by answering the following questions:</h3>
              {projectQuestions.map((question, index) => {
                const userAnswer = userAnswers.find(answer => answer.questionId === question.id);
    
                return (
                  <div key={question.id} className="space-y-2">
                    <div className={index === userAnswers.length ? "font-bold mt-10 flex" : "text-sm flex"}>
                      <QuestionIcons type={question.type} /> 
                      <p className="pl-2">{question.title}</p>
                    </div>
    
                    {index === userAnswers.length && (
                      <div className="flex mb-20">
                        {question.type === 'description' ? (
                          <textarea
                            value={currentAnswer}
                            onChange={(e) => setCurrentAnswer(e.target.value)}
                            data-amp-track-section="Project Creation"
                            data-amp-track-action="Project_Answer_Change"
                            data-amp-track-description="Project Answer Change"
                            placeholder="Your answer"
                            rows={3}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        ) : (
                          <input
                            value={currentAnswer}
                            onChange={(e) => setCurrentAnswer(e.target.value)}
                            data-amp-track-section="Project Creation"
                            data-amp-track-action="Project_Answer_Change"
                            data-amp-track-description="Project Answer Change"
                            placeholder="Your answer"
                            className="w-full p-2 border-solid border border-gray-300 rounded-md"
                          />
                        )}
                        <button 
                          onClick={handleAnswerQuestion}
                          data-amp-track-section="Project Creation"
                          data-amp-track-action="Submit_Click"
                          data-amp-track-description="Submit Project Answer"
                          className="bg-sky-500 hover:bg-sky-600 text-white font-semibold py-2 px-4 rounded-md"
                        >Submit</button>
                      </div>
                    )}
    
                    {userAnswer && (
                      <p className="text-gray-600 text-sm">
                        <strong>Answer:</strong> {userAnswer.answer}
                      </p>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </>
    );    
  }

  return null
}